import React from 'react';
import { Box, Text, HStack, Badge, Button } from '@chakra-ui/react';
import { ITableResponse, ICatalogResponse, CellRendering, CELL_RENDERING } from '../../../../models/sessions';
import { HeatmapCell } from '../HeatmapCell';
import { SparkBar } from './SparkBar';

interface WeekMatrixViewProps {
  table: ITableResponse;
  catalog: ICatalogResponse;
  cellRendering?: CellRendering;
  onCellRenderingChange?: (cr: CellRendering) => void;
  secondaryKpiId?: string;
}

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/**
 * Modalità WEEK_MATRIX — 7 giorni × 1 KPI cella + Sum + Peak.
 * Cella picco evidenziata con bordo viola + ★.
 */
export const WeekMatrixTableView: React.FC<WeekMatrixViewProps> = ({
  table,
  catalog,
  cellRendering = CELL_RENDERING.VALUE,
  onCellRenderingChange,
  secondaryKpiId,
}) => {
  const meta = table.meta as any;
  const days: string[] = meta.days ?? [];
  const kpiId: string = meta.kpiMetric ?? 'td';
  const kpiDef = catalog.metrics.find((m) => m.id === kpiId);
  const secondaryDef = secondaryKpiId
    ? catalog.metrics.find((m) => m.id === secondaryKpiId)
    : undefined;

  return (
    <Box>
      <HStack p={3} gap={3} bg="purple.50" borderBottom="1px solid" borderColor="purple.200" flexWrap="wrap">
        <Badge colorPalette="purple" fontSize="md" px={3} py={1}>
          KPI: {kpiDef?.label ?? kpiId}
        </Badge>
        <Text fontSize="sm" color="gray.700">
          Distribuzione 7 giorni · valori {kpiDef?.unit}
          {table.meta.unitMode === 'per_min' && kpiDef?.isVolumeMetric ? '/min' : ''}
        </Text>
        {/* Cell Rendering Toggle (Addendum v2 Parte 17) */}
        {onCellRenderingChange && (
          <HStack gap={1} ml="auto">
            <Text fontSize="xs" color="gray.600" fontWeight="600">Cella:</Text>
            <Button
              size="xs"
              variant={cellRendering === CELL_RENDERING.VALUE ? 'solid' : 'outline'}
              colorScheme={cellRendering === CELL_RENDERING.VALUE ? 'purple' : 'gray'}
              onClick={() => onCellRenderingChange(CELL_RENDERING.VALUE)}
            >
              Valore
            </Button>
            <Button
              size="xs"
              variant={cellRendering === CELL_RENDERING.DUAL ? 'solid' : 'outline'}
              colorScheme={cellRendering === CELL_RENDERING.DUAL ? 'purple' : 'gray'}
              onClick={() => onCellRenderingChange(CELL_RENDERING.DUAL)}
            >
              Dual
            </Button>
            <Button
              size="xs"
              variant={cellRendering === CELL_RENDERING.SPARK ? 'solid' : 'outline'}
              colorScheme={cellRendering === CELL_RENDERING.SPARK ? 'purple' : 'gray'}
              onClick={() => onCellRenderingChange(CELL_RENDERING.SPARK)}
            >
              Spark
            </Button>
          </HStack>
        )}
      </HStack>

      {table.rows.length === 0 ? (
        <Box p={8} textAlign="center" color="gray.500">
          Nessuna sessione nel periodo selezionato
        </Box>
      ) : cellRendering === CELL_RENDERING.SPARK ? (
        // Modalità SPARK: 1 colonna sparkline + Sum + Peak
        <Box overflowX="auto" bg="var(--c-card-bg)">
          <Box as="table" width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'var(--c-navy-1)', color: 'var(--c-card-bg)' }}>
                <th style={{ padding: 10, textAlign: 'left', minWidth: 200 }}>Atleta</th>
                <th style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>
                  Distribuzione 7gg
                </th>
                <th style={{ padding: 8, textAlign: 'center', fontSize: 10, background: '#475569' }}>
                  Σ Somma
                </th>
                <th style={{ padding: 8, textAlign: 'center', fontSize: 10, background: '#475569' }}>
                  ★ Picco
                </th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, idx) => {
                const peakDay = (row as any)._peakDay;
                const peakIdx = peakDay ? days.indexOf(peakDay) : undefined;
                const dailyVals = days.map((d) => row.values[d]);
                return (
                  <tr key={row.athleteId} style={{ background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)' }}>
                    <td style={{ padding: 10, fontWeight: 500 }}>
                      {row.athleteName}
                      {row.position && (
                        <Text fontSize="xs" color="gray.500">{row.position}</Text>
                      )}
                    </td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <SparkBar
                        values={dailyVals}
                        peakIndex={peakIdx !== undefined && peakIdx >= 0 ? peakIdx : undefined}
                      />
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, background: '#f8fafc' }}>
                      {row.values[`${kpiId}_sum`]?.toLocaleString('it-IT') ?? '—'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, color: '#6d28d9', background: '#f8fafc' }}>
                      {row.values[`${kpiId}_peak`]?.toLocaleString('it-IT') ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      ) : (
        <Box overflowX="auto" bg="var(--c-card-bg)">
          <Box as="table" width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'var(--c-navy-1)', color: 'var(--c-card-bg)' }}>
                <th style={{ padding: 10, textAlign: 'left', minWidth: 200, position: 'sticky', left: 0, background: '#334155' }}>
                  Atleta
                </th>
                {days.map((d, i) => (
                  <th
                    key={d}
                    style={{
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: 11,
                      minWidth: 80,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 11 }}>
                      {DAY_LABELS[i] ?? '?'}
                    </div>
                    <div style={{ fontSize: 9, opacity: 0.7, fontFamily: 'monospace' }}>
                      {d.slice(5)}
                    </div>
                  </th>
                ))}
                <th style={{ padding: 8, textAlign: 'center', fontSize: 10, background: '#475569' }}>
                  Σ Somma
                </th>
                <th style={{ padding: 8, textAlign: 'center', fontSize: 10, background: '#475569' }}>
                  ★ Picco
                </th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, idx) => {
                const peakDay = (row as any)._peakDay;
                return (
                  <tr key={row.athleteId} style={{ background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)' }}>
                    <td style={{ padding: 10, fontWeight: 500, position: 'sticky', left: 0, background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)', borderRight: '1px solid #e5e7eb' }}>
                      {row.athleteName}
                      {row.position && (
                        <Text fontSize="xs" color="gray.500">{row.position}</Text>
                      )}
                    </td>
                    {days.map((d) => {
                      const v = row.values[d];
                      const isPeak = peakDay === d && v !== undefined;
                      return (
                        <td
                          key={d}
                          style={{
                            padding: 4,
                            borderBottom: '1px solid #e5e7eb',
                            border: isPeak ? '2px solid #6d28d9' : undefined,
                            background: isPeak ? '#ede9fe' : undefined,
                            position: 'relative',
                          }}
                        >
                          {isPeak && (
                            <span style={{
                              position: 'absolute',
                              top: 1,
                              right: 2,
                              fontSize: 9,
                              color: '#6d28d9',
                            }}>★</span>
                          )}
                          {v === undefined ? (
                            <Box textAlign="center" color="gray.300" fontSize="11px" fontStyle="italic">
                              riposo
                            </Box>
                          ) : cellRendering === CELL_RENDERING.DUAL && secondaryDef ? (
                            <Box textAlign="center" lineHeight="1.2">
                              <Box fontWeight="700" fontSize="13px" fontFamily="monospace">
                                {v.toLocaleString('it-IT', {maximumFractionDigits: kpiDef?.decimals ?? 0})}
                              </Box>
                              <Box fontSize="10px" color="gray.500" fontFamily="monospace">
                                {row.values[`${secondaryDef.id}_${d}`]?.toLocaleString('it-IT') ?? '—'} {secondaryDef.unit}
                              </Box>
                            </Box>
                          ) : (
                            <HeatmapCell
                              value={v}
                              strategy={undefined}
                              decimals={kpiDef?.decimals ?? 0}
                            />
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, background: '#f8fafc' }}>
                      {row.values[`${kpiId}_sum`]?.toLocaleString('it-IT') ?? '—'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, color: '#6d28d9', background: '#f8fafc' }}>
                      {row.values[`${kpiId}_peak`]?.toLocaleString('it-IT') ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
};
